import React, {
  useEffect,
  useMemo,
  useRef,
  useState,

} from "react";

import {
  useNavigate,
  useParams,
  useLocation,
} from "react-router-dom";

import {
  useDispatch,
  useSelector,
} from "react-redux";

import { fetchMenu } from "../../actions/menuActions";
import { useCart } from "../../components/CartContext";

import "./css/HomeScreen.css";

const HERO_IMAGE =
  "https://images.unsplash.com/photo-1547592180-85f173990554?auto=format&fit=crop&w=2200&q=90";

const STORY_IMAGE =
  "https://images.unsplash.com/photo-1515003197210-e0cd71810b5f?auto=format&fit=crop&w=1400&q=85";

const PHILOSOPHY_IMAGE =
  "https://images.unsplash.com/photo-1547592180-85f173990554?auto=format&fit=crop&w=1400&q=85";

const GALLERY_IMAGES = [
  "https://images.unsplash.com/photo-1540189549336-e6e99c3679fe?auto=format&fit=crop&w=1200&q=85",
  "https://images.unsplash.com/photo-1544025162-d76694265947?auto=format&fit=crop&w=1200&q=85",
  "https://images.unsplash.com/photo-1533777857889-4be7c70b33f7?auto=format&fit=crop&w=1200&q=85",
  "https://images.unsplash.com/photo-1512621776951-a57141f2eefd?auto=format&fit=crop&w=1200&q=85",
  "https://images.unsplash.com/photo-1547592180-85f173990554?auto=format&fit=crop&w=1200&q=85",
  "https://images.unsplash.com/photo-1551024506-0bccd828d307?auto=format&fit=crop&w=1200&q=85",
];

function getImageUrl(image) {
  if (!image) {
    return "";
  }

  if (
    image.startsWith("http://") ||
    image.startsWith("https://")
  ) {
    return image;
  }

  return `${image}`;
}

function FeaturedMenuCard({ item, onAdd }) {
  return (
    <article className="theme5-featured-card">
      <div className="theme5-featured-image">
        {item.image ? (
          <img
            src={getImageUrl(item.image)}
            alt={item.name}
          />
        ) : (
          <div className="theme5-no-image">
            NO IMAGE
          </div>
        )}
      </div>

      <div className="theme5-featured-content">
        <div className="theme5-featured-top">
          <div className="theme5-featured-label-wrapper">
            <span className="theme5-featured-label">
              CHEF'S CHOICE
            </span>

            {item.categoryName && (
              <small>
                {item.categoryName}
              </small>
            )}
          </div>

          <span className="theme5-featured-price">
            ${Number(item.price || 0).toFixed(2)}
          </span>
        </div>

        <h3>{item.name}</h3>

        <p>
          {item.description || ""}
        </p>

        <button
          type="button"
          onClick={() => onAdd(item)}
        >
          ADD TO ORDER
        </button>
      </div>
    </article>
  );
}

function MenuCard({ item, onAdd }) {
  return (
    <article className="theme5-menu-card">
      <div className="theme5-menu-image">
        {item.image ? (
          <img
            src={getImageUrl(item.image)}
            alt={item.name}
          />
        ) : (
          <div className="theme5-no-image">
            NO IMAGE
          </div>
        )}
      </div>

      <div className="theme5-menu-content">
        <div className="theme5-menu-heading">
          <h4>{item.name}</h4>

          <span>
            ${Number(item.price || 0).toFixed(2)}
          </span>
        </div>

        {item.description && (
          <p>{item.description}</p>
        )}

        <button
          type="button"
          onClick={() => onAdd(item)}
        >
          ADD
        </button>
      </div>
    </article>
  );
}

function HomeScreen() {
  const {
    restaurantId,
    tableId,
  } = useParams();

  const navigate = useNavigate();
  const dispatch = useDispatch();
  const location = useLocation();

  const {
    addToCart,
    cartCount,
    cartTotal,
  } = useCart();

  const themeState = useSelector(
    (state) => state.theme
  );

  const menuState = useSelector(
    (state) => state.menu
  );

  const restaurantName =
    themeState?.restaurantName ||
    themeState?.restaurant_name ||
    "INDIAN CUISINE";

  const restaurantAddress =
    themeState?.address || "";

  const restaurantMobile =
    themeState?.mobile || "";

  const restaurantEmail =
    themeState?.email || "";

  const menuData =
    menuState?.menu || menuState;

  const [
    activeCategory,
    setActiveCategory,
  ] = useState(null);

  const [
    mobileMenuOpen,
    setMobileMenuOpen,
  ] = useState(false);

  const featuredSliderRef =
    useRef(null);

  useEffect(() => {
    if (!restaurantId) {
      return;
    }

    dispatch(
      fetchMenu(restaurantId)
    );
  }, [
    dispatch,
    restaurantId,
  ]);

  /*
=======================================================
GLOBAL HEADER SECTION NAVIGATION

This allows:

Cart → MENU
Cart → ABOUT
Cart → GALLERY
Cart → CATERING
Cart → CONTACT

and the same from:

Checkout
Order
Payment
Success

The Header sends a URL such as:

#theme5-menu

HomeScreen waits until the section exists and
then scrolls to it.
=======================================================
*/

useEffect(() => {

  if (!location.hash) {
    return;
  }

  const sectionId =
    location.hash.substring(1);

  let attempts = 0;

  const maxAttempts = 30;

  const scrollToRequestedSection = () => {

    const element =
      document.getElementById(sectionId);

    if (element) {

      const headerHeight =
        window.innerWidth <= 750
          ? 68
          : 82;

      const elementPosition =
        element.getBoundingClientRect().top +
        window.pageYOffset;

      window.scrollTo({

        top:
          elementPosition -
          headerHeight -
          10,

        behavior:
          "smooth",

      });

      return;
    }


    /*
    The HomeScreen may still be rendering.

    Try again every 100ms.
    */

    attempts += 1;

    if (attempts < maxAttempts) {

      setTimeout(
        scrollToRequestedSection,
        100
      );

    }

  };


  const timer = setTimeout(
    scrollToRequestedSection,
    100
  );


  return () => {

    clearTimeout(timer);

  };

}, [location.hash]);

  const categories = useMemo(() => {
    const backendCategories =
      Array.isArray(menuData?.categories)
        ? menuData.categories
        : [];

    return backendCategories
      .filter(
        (category) =>
          category?.is_active !== false
      )
      .sort(
        (a, b) =>
          Number(a.display_order || 0) -
          Number(b.display_order || 0)
      )
      .map((category) => ({
        ...category,

        items: (
          Array.isArray(category.items)
            ? category.items
            : []
        )
          .filter(
            (item) =>
              item?.is_available !== false
          )
          .sort(
            (a, b) =>
              Number(a.display_order || 0) -
              Number(b.display_order || 0)
          ),
      }));
  }, [menuData]);

  useEffect(() => {
    if (categories.length === 0) {
      return;
    }

    const categoryStillExists =
      categories.some(
        (category) =>
          category.id === activeCategory
      );

    if (
      !activeCategory ||
      !categoryStillExists
    ) {
      setActiveCategory(
        categories[0].id
      );
    }
  }, [
    categories,
    activeCategory,
  ]);

  const currentCategory =
    categories.find(
      (category) =>
        category.id === activeCategory
    ) ||
    categories[0] ||
    null;

  const featuredItems = useMemo(() => {
    return categories.flatMap(
      (category) => {
        const selectedItems = (
          category.items || []
        )
          .filter(
            (item) =>
              Number(
                item.display_order
              ) === 1 ||
              Number(
                item.display_order
              ) === 2
          )
          .sort(
            (a, b) =>
              Number(
                a.display_order || 0
              ) -
              Number(
                b.display_order || 0
              )
          )
          .slice(0, 2);

        return selectedItems.map(
          (item) => ({
            ...item,
            categoryName:
              category.name,
          })
        );
      }
    );
  }, [categories]);

  useEffect(() => {
    if (
      featuredItems.length <= 1 ||
      !featuredSliderRef.current
    ) {
      return;
    }

    const slider =
      featuredSliderRef.current;

    const interval = setInterval(() => {
      const cards =
        slider.querySelectorAll(
          ".theme5-featured-card"
        );

      if (!cards.length) {
        return;
      }

      const firstCard = cards[0];

      const cardWidth =
        firstCard.offsetWidth;

      const computedStyle =
        window.getComputedStyle(
          slider
        );

      const gap =
        parseFloat(
          computedStyle.columnGap ||
          computedStyle.gap ||
          "0"
        );

      const scrollAmount =
        cardWidth + gap;

      const maxScroll =
        slider.scrollWidth -
        slider.clientWidth;

      if (
        slider.scrollLeft +
          scrollAmount >=
        maxScroll - 5
      ) {
        slider.scrollTo({
          left: 0,
          behavior: "smooth",
        });
      } else {
        slider.scrollBy({
          left: scrollAmount,
          behavior: "smooth",
        });
      }
    }, 3000);

    return () => {
      clearInterval(interval);
    };
  }, [featuredItems]);

  const handleAddToCart =
    addToCart;

  const scrollToMenu = () => {
    document
      .getElementById("theme5-menu")
      ?.scrollIntoView({
        behavior: "smooth",
      });
  };

  const handleCategoryClick = (
    categoryId
  ) => {
    setActiveCategory(categoryId);

    setTimeout(() => {
      document
        .getElementById(
          "theme5-menu-items"
        )
        ?.scrollIntoView({
          behavior: "smooth",
          block: "start",
        });
    }, 50);
  };

  const handleCartClick = () => {
    navigate(
      `/${restaurantId}/table/${tableId}/cart`
    );
  };

  const closeMobileMenu = () => {
    setMobileMenuOpen(false);
  };

  return (
    <div className="theme5-home">

      {mobileMenuOpen && (
        <div className="theme5-mobile-nav">
          <button
            type="button"
            onClick={closeMobileMenu}
          >
            ×
          </button>

          <a
            href="#theme5-story"
            onClick={closeMobileMenu}
          >
            ABOUT
          </a>

          <a
            href="#theme5-menu"
            onClick={closeMobileMenu}
          >
            MENU
          </a>

          <a
            href="#theme5-gallery"
            onClick={closeMobileMenu}
          >
            GALLERY
          </a>

          <a
            href="#theme5-catering"
            onClick={closeMobileMenu}
          >
            CATERING
          </a>

          <a
            href="#theme5-contact"
            onClick={closeMobileMenu}
          >
            CONTACT
          </a>

          <button
            type="button"
            className="theme5-mobile-order"
            onClick={() => {
              closeMobileMenu();
              handleCartClick();
            }}
          >
            VIEW ORDER
          </button>
        </div>
      )}

      <section
        className="theme5-hero"
        style={{
          backgroundImage:
            `url("${HERO_IMAGE}")`,
        }}
      >
        <div className="theme5-hero-overlay" />

        <div className="theme5-hero-content">
          <span className="theme5-hero-small">
            AUTHENTIC INDIAN CUISINE
          </span>

          <div className="theme5-hero-logo">
            <span>
              {restaurantName}
            </span>

            <small>
              INDIAN KITCHEN
            </small>
          </div>

          <div className="theme5-hero-line" />

          <h1>
            GOOD FOOD.
            <br />
            GOOD FRIENDS.
            <br />
            GOOD TIMES.
          </h1>

          <p>
            Authentic Indian flavours, warm hospitality
            and a table made for sharing.
          </p>

          <button
            type="button"
            onClick={scrollToMenu}
            className="theme5-hero-button"
          >
            EXPLORE OUR MENU
          </button>
        </div>

        <div className="theme5-hero-bottom">
          <span>
            SCROLL TO EXPLORE
          </span>

          <span>↓</span>
        </div>
      </section>

      <section
        className="theme5-intro"
        id="theme5-story"
      >
        <div className="theme5-section-container">
          <div className="theme5-intro-label">
            OUR STORY
          </div>

          <div className="theme5-intro-grid">
            <div className="theme5-intro-heading">
              <h2>
                A LITTLE TASTE
                <br />
                OF INDIA.
              </h2>
            </div>

            <div className="theme5-intro-text">
              <p>
                A table is better when
                everyone is welcome.
              </p>

              <p>
                Inspired by the sun-drenched
                islands of India, our kitchen
                brings together fresh ingredients,
                traditional recipes and a modern
                Indian culinary traditions.
              </p>

              <p>
                From the first bite to the last
                glass raised around the table,
                every visit is about good food,
                good company and Indian
                hospitality.
              </p>
            </div>
          </div>
        </div>
      </section>

      <section className="theme5-story-image">
        <img
          src={STORY_IMAGE}
          alt="Indian dining"
        />

        <div className="theme5-story-image-caption">
          <span>TASTE INDIA</span>
          <span>EAT GREAT</span>
        </div>
      </section>

      {featuredItems.length > 0 && (
        <section className="theme5-featured">
          <div className="theme5-section-container">
            <div className="theme5-section-heading">
              <span>
                FROM OUR KITCHEN
              </span>

              <h2>
                FAVOURITES
              </h2>

              <p>
                Two favourites from every
                category, selected directly
                from our menu.
              </p>
            </div>

            <div
              ref={featuredSliderRef}
              className="theme5-featured-slider"
            >
              {featuredItems.map(
                (item, index) => (
                  <FeaturedMenuCard
                    key={`${item.id}-${index}`}
                    item={item}
                    onAdd={
                      handleAddToCart
                    }
                  />
                )
              )}
            </div>
          </div>
        </section>
      )}

      <section className="theme5-philosophy">
        <div className="theme5-philosophy-image">
          <img
            src={PHILOSOPHY_IMAGE}
            alt="Indian Indian food"
          />
        </div>

        <div className="theme5-philosophy-content">
          <span>
            OUR PHILOSOPHY
          </span>

          <h2>
            SIMPLE.
            <br />
            FRESH.
            <br />
            INDIAN.
          </h2>

          <p>
            We keep things simple.
            <br />
            <br />
            Quality ingredients.
            Traditional recipes.
            Food cooked with care.
            <br />
            <br />
            Because when the ingredients
            are good, you don't need to
            complicate things.
          </p>
        </div>
      </section>

      <section
        className="theme5-menu"
        id="theme5-menu"
      >
        <div className="theme5-section-container">
          <div className="theme5-section-heading theme5-menu-heading">
            <h2>
              THE MENU
            </h2>




          </div>

          {categories.length > 0 && (
            <div className="theme5-category-nav">
              {categories.map(
                (category) => (
                  <button
                    key={category.id}
                    type="button"
                    className={
                      activeCategory ===
                      category.id
                        ? "theme5-category-active"
                        : ""
                    }
                    onClick={() =>
                      handleCategoryClick(
                        category.id
                      )
                    }
                  >
                    {category.name}
                  </button>
                )
              )}
            </div>
          )}

          {currentCategory && (
            <div
              className="theme5-current-category"
              id="theme5-menu-items"
            >
              <div className="theme5-category-intro">
                <div>
                  <span>
                    OUR MENU
                  </span>

                  <h3>
                    {currentCategory.name}
                  </h3>
                </div>

                {currentCategory.description && (
                  <p>
                    {
                      currentCategory.description
                    }
                  </p>
                )}
              </div>

              {currentCategory.items?.length >
              0 ? (
                <div className="theme5-menu-grid">
                  {currentCategory.items.map(
                    (item) => (
                      <MenuCard
                        key={item.id}
                        item={item}
                        onAdd={
                          handleAddToCart
                        }
                      />
                    )
                  )}
                </div>
              ) : (
                <div className="theme5-no-items">
                  <p>
                    No items available
                    in this category.
                  </p>
                </div>
              )}
            </div>
          )}
        </div>
      </section>
            <section
        className="theme5-catering"
        id="theme5-catering"
      >
        <div className="theme5-section-container">
          <div className="theme5-catering-box">
            <span>
              BRING INDIAN CULTURE TO THE PARTY
            </span>

            <h2>
              CATERING &
              <br />
              PARTY TRAYS
            </h2>

            <p>
              Planning a celebration,
              office gathering or special
              event?
              <br />
              <br />
              Let us bring the flavours of
              India to your table.
            </p>

            <button
              type="button"
              onClick={() =>
                document
                  .getElementById(
                    "theme5-opa"
                  )
                  ?.scrollIntoView({
                    behavior: "smooth",
                  })
              }
            >
              DISCOVER THE INDIAN WAY
            </button>
          </div>
        </div>
      </section>

      <section
        className="theme5-gallery"
        id="theme5-gallery"
      >
        <div className="theme5-section-container">
          <div className="theme5-section-heading">
            <span>
              AROUND THE TABLE
            </span>

            <h2>
              GALLERY
            </h2>
          </div>

          <div className="theme5-gallery-grid">
            {GALLERY_IMAGES.map(
              (image, index) => (
                <div
                  className={
                    `theme5-gallery-item ${
                      index === 0
                        ? "theme5-gallery-large"
                        : ""
                    }`
                  }
                  key={`${image}-${index}`}
                >
                  <img
                    src={image}
                    alt={
                      `Restaurant gallery ${
                        index + 1
                      }`
                    }
                  />
                </div>
              )
            )}
          </div>
        </div>
      </section>

      {/* =================================================
    NAMASTE! MOMENTS
================================================= */}

<section
  className="theme5-opa"
  id="theme5-opa"
>

  <div className="theme5-section-container">

    <div className="theme5-opa-header">

      <span>
        NAMASTE! MOMENTS
      </span>

      <h2>
        MAKE EVERY MOMENT
        <br />
        AN NAMASTE! MOMENT.
      </h2>

      <p>
        Indian food is more than what is on the plate.
        It is about gathering around the table,
        sharing stories, laughing loudly and staying
        a little longer.
      </p>

    </div>


    <div className="theme5-opa-grid">


      {/* =================================================
          FOOD
      ================================================= */}

      <div className="theme5-opa-card">

        <div className="theme5-opa-image">

          <img
            src="https://images.unsplash.com/photo-1547592180-85f173990554?auto=format&fit=crop&w=1200&q=80"
            alt="Indian food"
          />

        </div>

        <div className="theme5-opa-card-content">

          <span>
            01
          </span>

          <h3>
            EAT
          </h3>

          <p>
            Fresh ingredients, generous plates
            and authentic Indian flavours.
          </p>

        </div>

      </div>


      {/* =================================================
          SHARE
      ================================================= */}

      <div className="theme5-opa-card">

        <div className="theme5-opa-image">

          <img
            src="https://images.unsplash.com/photo-1528605248644-14dd04022da1?auto=format&fit=crop&w=1200&q=80"
            alt="Friends sharing food"
          />

        </div>

        <div className="theme5-opa-card-content">

          <span>
            02
          </span>

          <h3>
            SHARE
          </h3>

          <p>
            Bring your people together and make
            every meal something worth remembering.
          </p>

        </div>

      </div>


      {/* =================================================
          CELEBRATE
      ================================================= */}

      <div className="theme5-opa-card">

        <div className="theme5-opa-image">

          <img
            src="https://images.unsplash.com/photo-1515003197210-e0cd71810b5f?auto=format&fit=crop&w=1200&q=80"
            alt="Indian restaurant dining"
          />

        </div>

        <div className="theme5-opa-card-content">

          <span>
            03
          </span>

          <h3>
            CELEBRATE
          </h3>

          <p>
            Good food. Good friends.
            Good times. That's the Indian way.
          </p>

        </div>

      </div>

    </div>


    <div className="theme5-opa-bottom">

      <span>
        TASTE INDIA
      </span>

      <b>
        •
      </b>

      <span>
        SHARE GREAT FOOD
      </span>

      <b>
        •
      </b>

      <span>
        SAY NAMASTE!
      </span>

    </div>

  </div>

</section>

      {cartCount > 0 && (
        <button
          type="button"
          className="theme5-floating-cart"
          onClick={handleCartClick}
        >
          <span>
            YOUR CART
          </span>

          <strong>
            {cartCount}
          </strong>

          <span>
            $
            {Number(
              cartTotal || 0
            ).toFixed(2)}
          </span>

          <b>→</b>
        </button>
      )}
    </div>
  );
}

export default HomeScreen;